// src/api/chatApi.js
// Sohbet ve sesli arama uçları (/api/chat). Sunucu sözleşmesi: docs/chat-api.md
import api, { apiGet, invalidateApiCache } from "./api";

const id = (value) => encodeURIComponent(String(value));
const clean = (params) =>
  Object.fromEntries(Object.entries(params || {}).filter(([, value]) => value !== "" && value != null));

export const chatApi = {
  conversations: async (config = {}) =>
    (await apiGet("/api/chat/conversations", { ...config, dedupe: false, cacheTtl: 0 })).data,
  // { type: "management" | "team" | "direct" | "group", user_id?, user_ids?, title?, team_id? }
  open: async (body) => (await api.post("/api/chat/conversations", body)).data,
  conversation: async (conversationId, config = {}) =>
    (await apiGet(`/api/chat/conversations/${id(conversationId)}`, { ...config, cacheTtl: 0 })).data,
  messages: async (conversationId, params = {}, config = {}) =>
    (await apiGet(`/api/chat/conversations/${id(conversationId)}/messages`, { ...config, dedupe: false, cacheTtl: 0, params: clean(params) })).data,
  send: async (conversationId, body) =>
    (await api.post(`/api/chat/conversations/${id(conversationId)}/messages`, body)).data,
  read: async (conversationId) => (await api.post(`/api/chat/conversations/${id(conversationId)}/read`)).data,
  remove: async (conversationId, messageId) =>
    (await api.delete(`/api/chat/conversations/${id(conversationId)}/messages/${id(messageId)}`)).data,
  leave: async (conversationId) => (await api.post(`/api/chat/conversations/${id(conversationId)}/leave`)).data,
  // Sohbeti sil: yalnız kendi görünümünden; çöp kutusuna yazılır.
  deleteConversation: async (conversationId) => (await api.delete(`/api/chat/conversations/${id(conversationId)}`)).data,
  // Kimden mesaj kabul ederim
  preferences: async () => (await apiGet("/api/chat/preferences", { cacheTtl: 0, dedupe: false })).data,
  setPreferences: async (acceptFrom) => (await api.patch("/api/chat/preferences", { accept_from: acceptFrom })).data,
  mute: async (conversationId, muted) => (await api.patch(`/api/chat/conversations/${id(conversationId)}/mute`, { muted })).data,
  directory: async (q = "", config = {}) =>
    (await apiGet("/api/chat/directory", { ...config, cacheTtl: 0, params: clean({ q }) })).data,
  unread: async (config = {}) => (await apiGet("/api/chat/unread-count", { ...config, cacheTtl: 0 })).data,

  // Sesli arama (socket yoksa REST yedeği)
  iceServers: async () => (await apiGet("/api/chat/ice-servers", { cacheTtl: 300000 })).data,
  activeCall: async () => (await apiGet("/api/chat/calls/active", { cacheTtl: 0, dedupe: false })).data,
  startCall: async (body) => (await api.post("/api/chat/calls", body)).data,
  acceptCall: async (callId, body) => (await api.post(`/api/chat/calls/${id(callId)}/accept`, body)).data,
  rejectCall: async (callId) => (await api.post(`/api/chat/calls/${id(callId)}/reject`)).data,
  endCall: async (callId, body = {}) => (await api.post(`/api/chat/calls/${id(callId)}/end`, body)).data,
  sendIce: async (callId, candidate) => (await api.post(`/api/chat/calls/${id(callId)}/ice`, { candidate })).data,

  // Zengin mesajlar
  respond: async (conversationId, messageId, body) =>
    (await api.post(`/api/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/respond`, body)).data,
  resolve: async (conversationId, messageId, body) =>
    (await api.post(`/api/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/resolve`, body)).data,
  uploadAudio: async (blob, { filename = "ses.webm", onProgress } = {}) => {
    const form = new FormData();
    form.append("audio", blob, filename);
    return (await api.post("/api/chat/uploads/audio", form, { timeout: 120000, onUploadProgress: onProgress || undefined })).data;
  },
  venues: async (q = "") => (await apiGet("/api/chat/venues", { cacheTtl: 60000, params: clean({ q }) })).data,
  uploadRecording: async (callId, blob, durationMs) => {
    const form = new FormData();
    form.append("audio", blob, "arama.webm");
    form.append("duration_ms", String(Math.round(durationMs || 0)));
    return (await api.post(`/api/chat/calls/${id(callId)}/recording`, form, { timeout: 300000 })).data;
  },
  // Genel eylem düğmesi çağrısı (api: { method, url, body })
  callAction: async (action) => {
    const method = String(action?.api?.method || "POST").toLowerCase();
    return (await api[method](action.api.url, action.api.body || {})).data;
  },

  // WhatsApp benzeri ekler (sunucu: services/chat/chatExtras.js)
  react: async (conversationId, messageId, emoji) =>
    (await api.post(`/api/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/reactions`, { emoji: emoji || "" })).data,
  edit: async (conversationId, messageId, body) =>
    (await api.patch(`/api/chat/conversations/${id(conversationId)}/messages/${id(messageId)}`, { body })).data,
  star: async (conversationId, messageId, on = true) =>
    (await api[on ? "put" : "delete"](`/api/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/star`)).data,
  starred: async () => (await apiGet("/api/chat/starred", { cacheTtl: 0, dedupe: false })).data,
  messageInfo: async (conversationId, messageId) =>
    (await apiGet(`/api/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/info`, { cacheTtl: 0, dedupe: false })).data,
  vote: async (conversationId, messageId, optionIds) =>
    (await api.post(`/api/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/vote`, { option_ids: optionIds })).data,
  forward: async (messageIds, conversationIds) => (await api.post("/api/chat/forward", { message_ids: messageIds, conversation_ids: conversationIds })).data,
  // { pinned?, archived?, marked_unread? }
  setState: async (conversationId, patch) => (await api.patch(`/api/chat/conversations/${id(conversationId)}/state`, patch)).data,
  pinMessage: async (conversationId, messageId) => (await api.put(`/api/chat/conversations/${id(conversationId)}/pin`, { message_id: messageId || null })).data,
  group: async (conversationId) => (await apiGet(`/api/chat/conversations/${id(conversationId)}/group`, { cacheTtl: 0, dedupe: false })).data,
  // { title?, description?, avatar_url?, only_admins_send?, only_admins_edit? }
  updateGroup: async (conversationId, patch) => (await api.patch(`/api/chat/conversations/${id(conversationId)}/group`, patch)).data,
  addMembers: async (conversationId, userIds) => (await api.post(`/api/chat/conversations/${id(conversationId)}/members`, { user_ids: userIds })).data,
  removeMember: async (conversationId, userId) => (await api.delete(`/api/chat/conversations/${id(conversationId)}/members/${id(userId)}`)).data,
  setMemberRole: async (conversationId, userId, role) => (await api.patch(`/api/chat/conversations/${id(conversationId)}/members/${id(userId)}`, { role })).data,
  media: async (conversationId) => (await apiGet(`/api/chat/conversations/${id(conversationId)}/media`, { cacheTtl: 0, dedupe: false })).data,
  search: async (q, conversationId) => (await apiGet("/api/chat/search", { cacheTtl: 0, dedupe: false, params: clean({ q, conversation_id: conversationId }) })).data,
  presence: async (userIds) => (await apiGet("/api/chat/presence", { cacheTtl: 0, dedupe: false, params: { user_ids: (userIds || []).join(",") } })).data,
  uploadFile: async (file, { onProgress } = {}) => {
    const form = new FormData();
    form.append("file", file, file.name || "dosya");
    return (await api.post("/api/chat/uploads/file", form, { timeout: 180000, onUploadProgress: onProgress || undefined })).data;
  },
  // Engelleme ve şikayet
  blocks: async () => (await apiGet("/api/chat/blocks", { cacheTtl: 0, dedupe: false })).data,
  block: async (userId) => (await api.post("/api/chat/blocks", { user_id: userId })).data,
  unblock: async (userId) => (await api.delete(`/api/chat/blocks/${id(userId)}`)).data,
  reportReasons: async () => (await apiGet("/api/chat/report-reasons", { cacheTtl: 300000 })).data,
  // { reason, details?, user_id?, conversation_id?, message_id?, block? }
  report: async (body) => (await api.post("/api/chat/reports", body)).data,

  // Hazır içerikler (yönetim hesapları; sunucu: services/chat/chatContent.js)
  // Seçilen günün maçları: { date, items: [...], duties: [{ key, label }] }
  contentMatches: async (params = {}) => (await apiGet("/api/chat/content/matches", { cacheTtl: 0, dedupe: false, params: clean(params) })).data,
  // { match_ids, date?, note? } → { message }
  sendMatches: async (conversationId, body) => (await api.post(`/api/chat/conversations/${id(conversationId)}/content/matches`, body)).data,
  // { title?, match_ids, duties, note?, deadline? } → { message }
  sendSignup: async (conversationId, body) => (await api.post(`/api/chat/conversations/${id(conversationId)}/content/signup`, body)).data,
  toggleSignup: async (conversationId, messageId, slotId, on) =>
    (await api.post(`/api/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/signup`, { slot_id: slotId, on: Boolean(on) })).data,
  assignSignup: async (conversationId, messageId, slotId, userId) =>
    (await api.post(`/api/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/signup/assign`, { slot_id: slotId, user_id: userId ?? null })).data,
  closeSignup: async (conversationId, messageId, closed = true) =>
    (await api.post(`/api/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/signup/close`, { closed: Boolean(closed) })).data,
  // { week, categories, league_id, season_id, start_date, end_date, squad_size, due_at, note } → { message }
  sendAward: async (conversationId, body) => (await api.post(`/api/chat/conversations/${id(conversationId)}/content/award`, body)).data,
  // { my_task_id, coordination_task_id, people: [{ user_id, name, task_id, submitted }], submitted, total }
  awardStatus: async (conversationId, messageId) =>
    (await apiGet(`/api/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/award`, { cacheTtl: 0, dedupe: false })).data,

  invalidate() {
    invalidateApiCache("api/chat");
  },
};

/**
 * Yönetim tarafı: aynı imzalar, /api/admin/chat uçları. Messenger, üyelerin
 * "ElitLig Yönetimi" yazışmalarını (Üye mesajları) bununla açar: ChatRoom
 * `api` özelliğiyle alır; okuma / gönderme / okundu yönetim uçlarına gider.
 */
export const adminChatApi = {
  ...chatApi,
  isAdmin: true,
  conversations: async (params = {}, config = {}) =>
    (await apiGet("/api/admin/chat/conversations", { ...config, dedupe: false, cacheTtl: 0, params: clean(params) })).data,
  open: async (body) => (await api.post("/api/admin/chat/conversations", body)).data,
  conversation: async (conversationId, config = {}) =>
    (await apiGet(`/api/admin/chat/conversations/${id(conversationId)}`, { ...config, cacheTtl: 0 })).data,
  messages: async (conversationId, params = {}, config = {}) =>
    (await apiGet(`/api/admin/chat/conversations/${id(conversationId)}/messages`, { ...config, dedupe: false, cacheTtl: 0, params: clean(params) })).data,
  send: async (conversationId, body) =>
    (await api.post(`/api/admin/chat/conversations/${id(conversationId)}/messages`, body)).data,
  read: async (conversationId) => (await api.post(`/api/admin/chat/conversations/${id(conversationId)}/read`)).data,
  respond: async (conversationId, messageId, body) =>
    (await api.post(`/api/admin/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/respond`, body)).data,
  resolve: async (conversationId, messageId, body) =>
    (await api.post(`/api/admin/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/resolve`, body)).data,
  directory: async (q = "", config = {}) =>
    (await apiGet("/api/admin/chat/directory", { ...config, cacheTtl: 0, params: clean({ q }) })).data,
  unread: async (config = {}) => {
    const data = await adminChatApi.conversations({ limit: 1 }, config);
    return { count: Number(data?.unread || 0) };
  },
  deleteConversation: async (conversationId) => (await api.delete(`/api/admin/chat/conversations/${id(conversationId)}`)).data,
  // Yalnız yönetimle yapılan aramalar; ses bağlantısı messages.listen yetkisiyle gelir.
  calls: async (params = {}) => (await apiGet("/api/admin/chat/calls", { cacheTtl: 0, dedupe: false, params: clean(params) })).data,
  stats: async () => (await apiGet("/api/admin/chat/stats", { cacheTtl: 0 })).data,
  // Gelen kutusu özeti: okunmamış / bekleyen talep sayıları, kategori dökümü.
  summary: async () => (await apiGet("/api/admin/chat/summary", { cacheTtl: 0, dedupe: false })).data,
  invalidate() {
    invalidateApiCache("api/chat");
    invalidateApiCache("api/admin/chat");
  },
};

const KNOWN = {
  VALIDATION_ERROR: "Lütfen mesaj alanını kontrol edin.",
  CONVERSATION_NOT_FOUND: "Sohbet bulunamadı.",
  CONVERSATION_FORBIDDEN: "Bu sohbete erişim yetkiniz yok.",
  CONVERSATION_LEFT: "Bu sohbetten ayrıldınız.",
  MESSAGE_NOT_FOUND: "Mesaj bulunamadı.",
  MESSAGE_FORBIDDEN: "Yalnızca kendi mesajınızı silebilirsiniz.",
  USER_NOT_FOUND: "Üye bulunamadı.",
  TEAM_FORBIDDEN: "Yalnızca kendi takımınızın grubunu açabilirsiniz.",
  TEAM_NOT_FOUND: "Takım bulunamadı.",
  CANNOT_LEAVE: "Bu sohbetten ayrılamazsınız.",
  CHAT_FORBIDDEN: "Bu işlem için yetkiniz yok.",
  CALL_NOT_ALLOWED: "Sesli arama yalnızca birebir sohbetlerde yapılabilir.",
  CALL_IN_PROGRESS: "Zaten devam eden bir aramanız var.",
  CALLEE_BUSY: "Aradığınız kişi şu anda başka bir görüşmede.",
  CALL_NOT_FOUND: "Arama bulunamadı.",
  CALL_NOT_RINGING: "Arama artık çalmıyor.",
  CALL_FORBIDDEN: "Bu aramaya erişiminiz yok.",
  CALLEE_UNAVAILABLE: "Şu anda yönetimden aranabilecek kimse yok.",
  OFFER_ALREADY_ANSWERED: "Bu teklif zaten yanıtlanmış.",
  OFFER_FORBIDDEN: "Kendi teklifinizi yanıtlayamazsınız.",
  CANNOT_WRITE: "Bu akışa mesaj yazılamaz.",
  UNSUPPORTED_AUDIO_TYPE: "Bu ses biçimi desteklenmiyor.",
  UPLOAD_UNAVAILABLE: "Ses yükleme deposu yapılandırılmamış.",
  PERMISSION_DENIED: "Bu işlem için yetkiniz yok.",
  CHAT_NOT_ACCEPTED: "Bu üye sizden mesaj kabul etmiyor.",
  CANNOT_DELETE: "Bu sohbet silinemez.",
  RECORDING_NOT_ALLOWED: "Yalnızca yönetimle yapılan aramalar kaydedilir.",
  AUTH_REQUIRED: "Oturumunuz sona ermiş görünüyor. Lütfen yeniden giriş yapın.",
  PIN_LIMIT: "En fazla 3 sohbet sabitlenebilir.",
  GROUP_ADMIN_ONLY: "Bu işlemi yalnız grup yöneticileri yapabilir.",
  EDIT_WINDOW_PASSED: "Mesajlar gönderildikten sonra 15 dakika içinde düzenlenebilir.",
  NOT_EDITABLE: "Yalnız metin mesajları düzenlenebilir.",
  MESSAGE_DELETED: "Bu mesaj silinmiş.",
  USER_BLOCKED: "Bu kişiyle mesajlaşma engelli.",
  CHAT_NOT_ALLOWED: "Bu kişiyle mesajlaşamazsınız.",
  GROUP_FULL: "Grup dolu.",
  NOT_A_GROUP: "Bu işlem yalnız gruplarda yapılabilir.",
  MEMBER_NOT_FOUND: "Kişi bu grupta değil.",
  UPLOAD_ERROR: "Dosya yüklenemedi.",
};

export const mapChatError = (error) => {
  const data = error?.response?.data || {};
  return KNOWN[data.code] || data.message || "İşlem tamamlanamadı. Lütfen tekrar deneyin.";
};

export const messageTime = (value) => {
  if (!value) return "";
  try { return new Date(value).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
};

// Liste satırındaki kısa zaman: bugün saat, dün "Dün", bu hafta gün adı, aksi halde tarih.
export const listTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "";
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (diffDays === 0) return messageTime(value);
  if (diffDays === 1) return "Dün";
  if (diffDays < 7) return date.toLocaleDateString("tr-TR", { weekday: "long" });
  return date.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export const dayLabel = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "";
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (diffDays === 0) return "Bugün";
  if (diffDays === 1) return "Dün";
  return date.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
};

export const ACCEPT_FROM_OPTIONS = [
  { key: "everyone", label: "Herkesten", hint: "Tüm takım yöneticileri ve oyuncular sana yazabilir." },
  { key: "teams", label: "Takım yöneticilerinden", hint: "Yalnızca takım yöneticileri." },
  { key: "players", label: "Oyunculardan", hint: "Yalnızca oyuncular." },
  { key: "teammates", label: "Takım arkadaşlarımdan", hint: "Kendi takımının yöneticisi ve oyuncuları." },
];

export const formatDurationMs = (ms) => {
  const total = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

// Konum mesajı için harita bağlantısı: koordinat varsa doğrudan, yoksa adres araması.
export const mapLinkFor = (location) => {
  if (!location) return null;
  if (location.lat != null && location.lng != null) return `https://www.google.com/maps?q=${location.lat},${location.lng}`;
  const q = [location.venue_name || location.label, location.address].filter(Boolean).join(" ");
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
};

export const initialsOf = (name) =>
  String(name || "?").split(" ").map((part) => part[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
